import { useState, useEffect, createContext, useContext } from "react";
import "@/App.css";
import { BrowserRouter, Routes, Route, useLocation, useNavigate } from "react-router-dom";
import { ClerkProvider, SignedIn, SignedOut, useAuth, useUser } from "@clerk/clerk-react";
import { Toaster } from "@/components/ui/sonner";
import CookieConsent from "@/components/ui/CookieConsent";
import HomePage from "@/pages/HomePage";
import ResultsPage from "@/pages/ResultsPage";
import HistoryPage from "@/pages/HistoryPage";
import SubscriptionSuccessPage from "@/pages/SubscriptionSuccessPage";
import TermsPage from "@/pages/TermsPage";
import PrivacyPage from "@/pages/PrivacyPage";

const CLERK_PUBLISHABLE_KEY = process.env.REACT_APP_CLERK_PUBLISHABLE_KEY;
const BACKEND_URL = (process.env.REACT_APP_BACKEND_URL || '').replace(/\/+$/, '');

// Auth Context for additional app state
const AppAuthContext = createContext(null);

export const useAppAuth = () => useContext(AppAuthContext);

const AppAuthProvider = ({ children }) => {
  const { getToken, isSignedIn } = useAuth();
  const { user } = useUser();
  const [promptsUsed, setPromptsUsed] = useState(0);
  const [promptLimit, setPromptLimit] = useState(2);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const fetchUserData = async () => {
      // Debug logging
      console.log("[AppAuthProvider] Checking auth status:", { isSignedIn, hasToken: !!getToken });

      if (!BACKEND_URL) {
        console.error("[AppAuthProvider] Missing REACT_APP_BACKEND_URL environment variable");
        setLoading(false);
        return;
      }

      if (isSignedIn) {
        try {
          const token = await getToken();
          console.log("[AppAuthProvider] Token retrieved:", token ? "Yes (length " + token.length + ")" : "No");

          const url = `${BACKEND_URL}/api/auth/me`;
          console.log("[AppAuthProvider] Fetching user data from:", url);

          const response = await fetch(url, {
            headers: {
              'Authorization': `Bearer ${token}`,
              'Content-Type': 'application/json',
            },
          });

          console.log("[AppAuthProvider] Response status:", response.status);

          if (response.ok) {
            const userData = await response.json();
            console.log("[AppAuthProvider] User data loaded:", userData);
            setPromptsUsed(userData.prompts_used_this_month || 0);
            setPromptLimit(userData.prompt_limit || 2);
          } else {
            console.error("[AppAuthProvider] Failed to fetch user data:", response.status, response.statusText);
            const text = await response.text();
            console.error("[AppAuthProvider] Response body:", text);
          }
        } catch (error) {
          console.error("[AppAuthProvider] Failed to fetch user data:", error);
        }
      }
      setLoading(false);
    };

    fetchUserData();
  }, [isSignedIn, getToken]);

  const incrementPrompts = () => {
    setPromptsUsed(prev => prev + 1);
  };

  const canUsePrompt = () => {
    return isSignedIn && promptsUsed < promptLimit;
  };

  const remainingPrompts = () => {
    return Math.max(0, promptLimit - promptsUsed);
  };

  return (
    <AppAuthContext.Provider value={{
      promptsUsed,
      promptLimit,
      incrementPrompts,
      canUsePrompt,
      remainingPrompts,
      loading
    }}>
      {children}
    </AppAuthContext.Provider>
  );
};

// Protected Route Component
const ProtectedRoute = ({ children }) => {
  const { isSignedIn, isLoaded } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();

  useEffect(() => {
    if (isLoaded && !isSignedIn) {
      navigate('/', { state: { from: location } });
    }
  }, [isSignedIn, isLoaded, navigate, location]);

  if (!isLoaded) {
    return (
      <div className="min-h-screen bg-obsidian-300 flex items-center justify-center">
        <div className="w-8 h-8 border-2 border-gold-500/30 border-t-gold-500 rounded-full animate-spin" />
      </div>
    );
  }

  if (!isSignedIn) {
    return null;
  }

  return children;
};

function AppRouter() {
  return (
    <Routes>
      <Route path="/" element={<HomePage />} />
      <Route path="/results" element={
        <ProtectedRoute>
          <ResultsPage />
        </ProtectedRoute>
      } />
      <Route path="/history" element={
        <ProtectedRoute>
          <HistoryPage />
        </ProtectedRoute>
      } />
      <Route path="/subscription/success" element={
        <ProtectedRoute>
          <SubscriptionSuccessPage />
        </ProtectedRoute>
      } />
      <Route path="/terms" element={<TermsPage />} />
      <Route path="/privacy" element={<PrivacyPage />} />
    </Routes>
  );
}

function App() {
  if (!CLERK_PUBLISHABLE_KEY) {
    return (
      <div className="min-h-screen bg-obsidian-300 flex items-center justify-center text-white">
        <div className="text-center">
          <h1 className="text-2xl font-bold mb-4">Configuration Error</h1>
          <p className="text-zinc-400">Missing REACT_APP_CLERK_PUBLISHABLE_KEY environment variable</p>
        </div>
      </div>
    );
  }

  if (!BACKEND_URL) {
    return (
      <div className="min-h-screen bg-obsidian-300 flex items-center justify-center text-white">
        <div className="text-center">
          <h1 className="text-2xl font-bold mb-4">Configuration Error</h1>
          <p className="text-zinc-400">Missing REACT_APP_BACKEND_URL environment variable</p>
        </div>
      </div>
    );
  }

  return (
    <ClerkProvider publishableKey={CLERK_PUBLISHABLE_KEY}>
      <div className="App min-h-screen dark">
        <BrowserRouter>
          <AppAuthProvider>
            <AppRouter />
          </AppAuthProvider>
        </BrowserRouter>
        <CookieConsent />
        <Toaster position="top-right" />
      </div>
    </ClerkProvider>
  );
}

export default App;
